import React, { useState, useCallback } from 'react';
import { fetchWithTimeout, ServerError, categorizeError } from '../utils/networkError';
import ApiErrorAlert from '../components/ApiErrorAlert';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

/**
 * Test page for verifying server error (500) handling functionality
 * This page demonstrates:
 * 1. Triggering a 500 Internal Server Error
 * 2. Displaying a user-friendly error message
 * 3. Not exposing technical details
 * 4. Providing a "Return Home" option
 */
const ServerErrorTest = () => {
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastAction, setLastAction] = useState('');

  // Trigger a real 500 error from the backend
  const trigger500Error = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setLastAction('Trigger 500 Server Error');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/test/error-500`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      // Check for server error status (5xx)
      if (response.status >= 500 && response.status < 600) {
        // Create a ServerError to trigger proper display
        throw new ServerError('Something went wrong on our end. Please try again later.', response.status);
      }

      if (response.ok) {
        const data = await response.json();
        setResult({ success: true, message: 'API call succeeded!', data });
      } else {
        setResult({ success: false, message: `API returned status ${response.status}` });
      }
    } catch (err) {
      console.log('Caught error:', err);
      // If it's already a ServerError, use it directly
      if (err instanceof ServerError) {
        setError(err);
      } else {
        // Wrap any other error that might be a server error
        setError(err);
      }
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, []);

  // Test a normal API call (should succeed)
  const testNormalCall = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setResult(null);
    setLastAction('Normal API Call (Health Check)');

    try {
      const response = await fetchWithTimeout(
        `${API_URL}/health`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        },
        15000
      );

      if (response.ok) {
        const data = await response.json();
        setResult({ success: true, message: 'API is healthy!', data });
      } else {
        setResult({ success: false, message: `API returned status ${response.status}` });
      }
    } catch (err) {
      console.log('Caught error:', err);
      setError(err);
    } finally {
      setIsLoading(false);
      setIsRetrying(false);
    }
  }, []);

  // Retry handler - tests normal API call to show recovery
  const handleRetry = useCallback(() => {
    setIsRetrying(true);
    testNormalCall();
  }, [testNormalCall]);

  // Get error info for display
  const errorInfo = error ? categorizeError(error) : null;

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
            Server Error (500) Test
          </h1>
          <p className="font-body text-slate-500 dark:text-silver/60">
            Test page for Feature #62: Server 500 error shows friendly message
          </p>
        </div>

        {/* Test Buttons */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6 mb-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-slate-500 dark:text-silver/60 mb-4">
            Test Actions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <button
              onClick={trigger500Error}
              disabled={isLoading}
              className="px-4 py-3 bg-purple-500/20 border border-purple-500/40 rounded-xl font-mono text-xs uppercase tracking-widest text-purple-400 hover:bg-purple-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && lastAction.includes('500') ? 'Loading...' : 'Trigger 500 Error'}
            </button>
            <button
              onClick={testNormalCall}
              disabled={isLoading}
              className="px-4 py-3 bg-green-500/20 border border-green-500/40 rounded-xl font-mono text-xs uppercase tracking-widest text-green-400 hover:bg-green-500/30 transition-smooth disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading && lastAction.includes('Health') ? 'Loading...' : 'Normal API Call'}
            </button>
          </div>
          <p className="font-body text-xs text-slate-400 dark:text-silver/40 mt-4">
            Click "Trigger 500 Error" to simulate an internal server error. The app should display a user-friendly message without technical details.
          </p>
        </div>

        {/* Last Action */}
        {lastAction && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-4 mb-6">
            <p className="font-mono text-xs uppercase tracking-widest text-slate-400 dark:text-silver/40">
              Last Action: {lastAction}
            </p>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <ApiErrorAlert
            error={error}
            onRetry={handleRetry}
            isRetrying={isRetrying}
            showHomeButton={true}
          />
        )}

        {/* Error Details (for verification) */}
        {errorInfo && (
          <div className="glass border border-light-border dark:border-border rounded-2xl p-4 mb-6">
            <h3 className="font-mono text-xs uppercase tracking-widest text-slate-500 dark:text-silver/60 mb-3">
              Feature #62 Verification Checklist
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <span className={errorInfo.type === 'server' ? 'text-green-400' : 'text-red-400'}>
                  {errorInfo.type === 'server' ? '✓' : '✗'}
                </span>
                <span className="text-light-text-secondary dark:text-silver/80">Error type detected as "server": {errorInfo.type}</span>
              </li>
              <li className="flex items-center gap-2">
                <span className={errorInfo.message && !errorInfo.message.includes('stack') && !errorInfo.message.includes('Error:') ? 'text-green-400' : 'text-red-400'}>
                  {errorInfo.message && !errorInfo.message.includes('stack') && !errorInfo.message.includes('Error:') ? '✓' : '✗'}
                </span>
                <span className="text-light-text-secondary dark:text-silver/80">User-friendly message (no technical details)</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span className="text-light-text-secondary dark:text-silver/80">"Return Home" button available for server errors</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <span className="text-light-text-secondary dark:text-silver/80">"Retry" button available</span>
              </li>
            </ul>
          </div>
        )}

        {/* Success Result */}
        {result && (
          <div className={`mb-6 p-4 ${result.success ? 'bg-green-500/10 border-green-500/30' : 'bg-yellow-500/10 border-yellow-500/30'} border rounded-xl`}>
            <p className={`${result.success ? 'text-green-400' : 'text-yellow-400'} text-sm font-medium mb-2`}>
              {result.success ? 'Success' : 'Response'}
            </p>
            <p className={`${result.success ? 'text-green-300/80' : 'text-yellow-300/80'} text-sm`}>
              {result.message}
            </p>
            {result.data && (
              <pre className="mt-3 text-xs text-slate-500 dark:text-silver/60 bg-black/20 p-3 rounded-lg overflow-x-auto">
                {JSON.stringify(result.data, null, 2)}
              </pre>
            )}
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-light-text dark:border-pure-white"></div>
            <span className="ml-3 font-mono text-sm text-slate-500 dark:text-silver/60">Processing...</span>
          </div>
        )}

        {/* Technical Info */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-slate-500 dark:text-silver/60 mb-4">
            Feature #62 Requirements
          </h2>
          <div className="space-y-3 font-body text-sm text-light-text-secondary dark:text-silver/80">
            <p>
              <strong className="text-light-text dark:text-pure-white">1. Trigger server error condition:</strong> The "/api/test/error-500" endpoint intentionally throws an error to simulate a 500 response.
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">2. Verify generic error message shows:</strong> The error message should be user-friendly: "Something went wrong on our end. Please try again later."
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">3. Verify no technical details exposed:</strong> No stack traces, error codes, or internal error messages should be visible to the user.
            </p>
            <p>
              <strong className="text-light-text dark:text-pure-white">4. Verify option to return home:</strong> A "Return Home" button should be available to navigate back to the homepage.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServerErrorTest;
